/**
 * Client-side environment variable validation.
 *
 * Required vars cause a hard error (caught by the ErrorBoundary).
 * Optional-but-important vars log a warning in development so missing
 * configuration is surfaced early rather than silently degrading features.
 *
 * Called once at app startup from main.jsx.
 */

const isDev = import.meta.env.DEV;

/**
 * Validate environment variables.
 * Throws if any required var is missing; warns about optional vars in dev.
 */
export function validateEnv() {
  // Required — app cannot function without these
  const required = [
    "VITE_SUPABASE_URL",
    "VITE_SUPABASE_ANON_KEY",
  ];

  const missing = required.filter((key) => !import.meta.env[key]);
  if (missing.length > 0) {
    throw new Error(
      `Missing required environment variables: ${missing.join(", ")}.\n` +
      "Copy .env.example to .env and fill in the values."
    );
  }

  // Optional — features degrade gracefully but warn in dev
  if (isDev) {
    const optional = [
      { key: "VITE_VAPID_PUBLIC_KEY", feature: "push notifications" },
      { key: "VITE_API_URL",         feature: "explicit API URL (defaults to same origin)" },
    ];

    for (const { key, feature } of optional) {
      if (!import.meta.env[key]) {
        console.warn(`[env] ${key} not set — ${feature} may not work correctly.`);
      }
    }
  }
}
